// @ts-nocheck
// =============================================================================
//  KitchenControl — Receptenbeheer Module
//  Props: { sb, tenantId, activeTab, onTabChange }
//  activeTab: 'recepten' | 'nieuw' | 'paste' | 'categorieen'
// =============================================================================
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  BookOpen, Plus, ClipboardPaste, Settings2,
  X, Trash2, Search, ChevronDown, ChevronRight,
  TriangleAlert, Save, Printer, Check, ChefHat, MoreHorizontal, Pencil, Thermometer, ArrowLeft,
} from "lucide-react";
import RecipePhoto, { RecipePhotoContext, RecipeThumbnail, PHOTO_BUCKET, photoPath } from "./RecipePhoto";
import { Whiteboard } from "./Whiteboard";
import { useBeheerPin } from "./ui/BeheerPin";

// ── Kleuren ───────────────────────────────────────────────────────────────────
const C = {
  bg:      "#F8FAFC",
  bg2:     "#FFFFFF",
  bg3:     "#F1F5F9",
  hdr:     "#0F172A",
  hdrBdr:  "#1E293B",
  txt:     "#0F172A",
  txt2:    "#475569",
  txt3:    "#94A3B8",
  bdr:     "#E2E8F0",
  bdr2:    "#CBD5E1",
  pri:     "#2563EB",
  priBg:   "#EFF6FF",
  priBd:   "#BFDBFE",
  green:   "#16A34A",
  greenBg: "#DCFCE7",
  greenBd: "#BBF7D0",
  red:     "#DC2626",
  redBg:   "#FEE2E2",
  redBd:   "#FCA5A5",
  amber:   "#D97706",
  amberBg: "#FFFBEB",
  amberBd: "#FDE68A",
};

// ── Constanten ────────────────────────────────────────────────────────────────
const uid = () => `${Date.now()}-${Math.floor(Math.random() * 9999)}`;
const LS_KEY = "kc-recepten-v1";

const ALLERGENEN_LIJST = [
  "Gluten","Schaaldieren","Eieren","Vis","Pindas","Soja","Melk","Noten",
  "Selderij","Mosterd","Sesamzaad","Zwaveldioxide","Lupine","Weekdieren",
];

const BEWAARADVIES_OPTIES = [
  "", "Bewaren onder 7°C", "Bewaren onder -18°C",
  "Bewaren op kamertemperatuur", "Direct serveren",
];

const UNITS = ["kg","g","L","ml","stuks","bos","blik","zak","el","tl","snuf","teen","takje","ons","overig"];

// ── Gedeelde stijlen ──────────────────────────────────────────────────────────
const inp = {
  background: C.bg2, border: `1px solid ${C.bdr}`, color: C.txt,
  padding: "7px 10px", fontFamily: "inherit", fontSize: 13,
  borderRadius: 6, outline: "none", width: "100%", boxSizing: "border-box",
};
const btnStyle = (bg, fg, bd) => ({
  background: bg, color: fg, border: `1px solid ${bd || bg}`,
  padding: "7px 14px", fontFamily: "inherit", fontSize: 12,
  fontWeight: 600, borderRadius: 6, cursor: "pointer", display: "flex",
  alignItems: "center", gap: 5,
});

function InklapSectie({ titel, status, open, onToggle, children }) {
  return (
    <div style={{ border: `1px solid ${C.bdr}`, borderRadius: 10, background: C.bg2, overflow: "hidden" }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          width: "100%",
          minHeight: 48,
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "11px 12px",
          background: open ? C.bg3 : C.bg2,
          border: "none",
          borderBottom: open ? `1px solid ${C.bdr}` : "none",
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "left",
        }}
      >
        <ChevronRight size={16} color={C.txt2} style={{ flexShrink: 0, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .12s" }} />
        <span style={{ flex: 1, fontSize: 12.5, fontWeight: 800, color: C.txt, textTransform: "uppercase", letterSpacing: 0.7 }}>{titel}</span>
        {status && <span style={{ fontSize: 11.5, color: C.txt3, fontWeight: 600, whiteSpace: "nowrap" }}>{status}</span>}
      </button>
      {open && <div style={{ padding: 12 }}>{children}</div>}
    </div>
  );
}

// ── Paste-parser (overgenomen uit BijHENC) ────────────────────────────────────
const PARSE_UNITS = ["kg","g","gr","gram","ml","l","dl","cl","liter","el","tl",
  "eetlepel","eetlepels","theelepel","theelepels","stuks","stuk","blik","blikken","teen","teentje",
  "teentjes","takje","takjes","snuf","snufje","ons","pond"];
const UNIT_NORM = {
  gr:"g", gram:"g", liter:"L", dl:"dl", cl:"cl", eetlepel:"el", eetlepels:"el",
  theelepel:"tl", theelepels:"tl", stuk:"stuks", blikken:"blik", teentje:"teen",
  teentjes:"teen", takjes:"takje", snufje:"snuf",
};
const RECEPT_KOPJES = new Set(["ingredient", "ingredienten", "ingrediënt", "ingrediënten", "bereiding", "werkwijze", "methode", "bereidingswijze"]);

function isReceptKopje(line) {
  const clean = line.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[:.]+$/g, "").trim();
  return RECEPT_KOPJES.has(clean);
}

function parseIngLine(line) {
  line = line.trim().replace(/^[-–•*]\s*/, "");
  if (!line || line.length < 2) return null;
  const numRx = /^(\d+(?:[.,]\d+)?)\s*/;
  const unitRxStr = "^(" + PARSE_UNITS.join("|") + ")\\b\\.?\\s*";
  const unitRx = new RegExp(unitRxStr, "i");
  let rest = line, amount = "", unit = "";
  const numMatch = rest.match(numRx);
  if (numMatch) {
    amount = numMatch[1].replace(",", ".");
    rest = rest.slice(numMatch[0].length);
    const um = rest.match(unitRx);
    if (um) { unit = UNIT_NORM[um[1].toLowerCase()] || um[1].toLowerCase(); rest = rest.slice(um[0].length); }
    else unit = "stuks";
  } else {
    const um = rest.match(unitRx);
    if (um) { unit = UNIT_NORM[um[1].toLowerCase()] || um[1].toLowerCase(); rest = rest.slice(um[0].length); }
    else return null;
  }
  const name = rest.trim();
  if (!name || name.length < 2) return null;
  return { id: uid(), naam: name, hoeveelheid: amount || "1", eenheid: unit };
}

function parsePasteRecept(text) {
  const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
  if (!lines.length) return null;
  const titel = lines[0];
  const ingredienten = [], bereidingLines = [];
  for (const line of lines.slice(1)) {
    if (isReceptKopje(line)) continue;
    const ing = parseIngLine(line);
    if (ing) ingredienten.push(ing);
    else {
      const stap = line.replace(/^[-–—•*]\s*/, "").trim();
      if (stap.length > 3 && !isReceptKopje(stap)) bereidingLines.push(stap);
    }
  }
  return { naam: titel, ingredienten, bereiding: bereidingLines.join("\n") };
}

// ── Allergeen-detectie (overgenomen uit BijHENC) ──────────────────────────────
const ALLERGEN_KEYWORDS = {
  Gluten: { include: ["tarwe","tarwemeel","bloem","bakmeel","brood","paneermeel","pasta","noedels","spaghetti","couscous","bulgur","spelt","rogge","gerst","haver","havermout","crackers","bladerdeeg","ketjap","sojasaus","wrap","tortilla"], exclude: [] },
  Schaaldieren: { include: ["garnaal","garnalen","scampi","langoustine","kreeft","krab","rivierkreeft","crevette"], exclude: [] },
  Eieren: { include: ["ei","eieren","eigeel","eidooier","eiwit","eipoeder","mayonaise","hollandaise","bearnaise","aioli","meringue","custard"], exclude: [] },
  Vis: { include: ["vis","tonijn","zalm","kabeljauw","schol","tong","wijting","makreel","haring","sardine","ansjovis","forel","zeebaars","dorade","vissaus","worcestershire","surimi","kaviaar"], exclude: [] },
  Pindas: { include: ["pinda","pindas","pindakaas","pindasaus","satésaus","aardnoot","arachide"], exclude: [] },
  Soja: { include: ["soja","sojaboon","sojasaus","ketjap","edamame","tofu","tempeh","miso","sojamelk","tahini"], exclude: [] },
  Melk: { include: ["melk","room","slagroom","crème fraîche","mascarpone","ricotta","mozzarella","parmezaan","kaas","boter","ghee","yoghurt","kwark","zuivel","melkpoeder","roomijs"], exclude: ["rijstmelk","sojamelk","amandelmelk","kokosmelk","havermelk","olijfboter","pindakaas","botersla"] },
  Noten: { include: ["amandel","amandelen","hazelnoot","walnoot","cashew","pecan","paranoot","pistache","macadamia","marsepein","nougat","pijnboompitten","noten"], exclude: ["nootmuskaat","kokosnoot"] },
  Selderij: { include: ["selderij","bleekselderij","knolselderij","selderie"], exclude: [] },
  Mosterd: { include: ["mosterd","mosterdzaad","dijon"], exclude: [] },
  Sesamzaad: { include: ["sesam","sesamzaad","sesamolie","tahini","tahin"], exclude: [] },
  Zwaveldioxide: { include: ["wijn","port","sherry","marsala","vermouth","azijn","balsamico","rozijnen","krenten","gedroogd fruit"], exclude: ["rijstazijn","appelazijn"] },
  Lupine: { include: ["lupine","lupinebonen","lupinemeel"], exclude: [] },
  Weekdieren: { include: ["mossel","mosselen","oester","oesters","inktvis","calamares","octopus","coquille","wulk","kokkel"], exclude: [] },
};

const GLUTEN_SUB = ["Tarwe", "Rogge", "Gerst", "Haver"];
const NOTEN_SUB = ["Amandelen", "Hazelnoten", "Walnoten", "Cashewnoten", "Pecannoten", "Paranoten", "Pistachenoten", "Macadamianoten"];

function detectAllergenen(ingredienten) {
  if (!Array.isArray(ingredienten) || !ingredienten.length) return [];
  const norm = s => (s||"").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/\s+/g," ").trim();
  const esc = s => s.replace(/[.*+?^${}()|[\]\\]/g,"\\$&");
  const wb = t => new RegExp(`\\b${esc(norm(t))}\\b`);
  const text = ingredienten.map(i => norm(i.naam)).join(" | ");
  const detected = [];

  for (const [allergeen, { include, exclude }] of Object.entries(ALLERGEN_KEYWORDS)) {
    const excl = exclude.some(t => wb(t).test(text));
    let found = include.some(t => wb(t).test(text));
    if (found && !excl) {
      detected.push(allergeen);
    } else if (found && excl) {
      const clean = exclude.reduce((acc, ex) => acc.replace(wb(ex)," "), text);
      if (include.some(t => wb(t).test(clean))) detected.push(allergeen);
    }
  }

  // Sub-detectie Gluten
  if (detected.includes("Gluten")) {
    const subMap = {
      Tarwe:  ["tarwe","tarwemeel","bloem","bakmeel","semolina","griesmeel","spelt","kamut","brood","paneermeel","pasta","noedels","spaghetti","couscous","bulgur","seitan","bladerdeeg","crackers","wrap","tortilla"],
      Rogge:  ["rogge","roggebrood","roggemeel"],
      Gerst:  ["gerst","gort","parelgort","mout"],
      Haver:  ["haver","havermout","havervlokken"],
    };
    for (const [sub, terms] of Object.entries(subMap)) {
      if (terms.some(t => wb(t).test(text))) detected.push(sub);
    }
  }

  // Sub-detectie Noten
  if (detected.includes("Noten")) {
    const subMap = {
      Amandelen:      ["amandel","amandelen","amandelmeel","marsepein","nougat","praline"],
      Hazelnoten:     ["hazelnoot","hazelnoten","nutella"],
      Walnoten:       ["walnoot","walnoten"],
      Cashewnoten:    ["cashew","cashewnoten"],
      Pecannoten:     ["pecan","pecannoten"],
      Paranoten:      ["paranoot","paranoten"],
      Pistachenoten:  ["pistache","pistaches"],
      Macadamianoten: ["macadamia"],
    };
    for (const [sub, terms] of Object.entries(subMap)) {
      if (terms.some(t => wb(t).test(text))) detected.push(sub);
    }
  }

  return detected;
}

// ── Supabase helpers ──────────────────────────────────────────────────────────
const LS_KEY_RECEPTEN = "kc-recepten-v1";

function loadLocal() {
  try { const s = localStorage.getItem(LS_KEY_RECEPTEN); if (s) return JSON.parse(s); } catch {}
  return null;
}
function saveLocal(state) {
  try { localStorage.setItem(LS_KEY_RECEPTEN, JSON.stringify(state)); } catch {}
}

async function sbLoad(sb, tenantId) {
  try {
    const rows = await sb.select("recepten_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
    if (rows?.length && rows[0]?.data) return rows[0].data;
  } catch (e) { console.warn("[Recepten] load failed:", e); }
  return null;
}

async function sbSave(sb, tenantId, data) {
  try {
    const rows = await sb.select("recepten_data", `tenant_id=eq.${tenantId}&select=data&limit=1`);
    // VEILIGHEIDSSLOT: een lege lijst (0 recepten) mag NOOIT een gevulde lijst op
    // de server overschrijven. Voorkomt dataverlies bij een seed/lege staat.
    if (!data?.recepten?.length && rows?.length && rows[0]?.data?.recepten?.length) {
      console.warn("[Recepten] save geblokkeerd: lege lijst zou een gevulde lijst overschrijven.");
      return;
    }
    if (rows?.length) {
      await sb.update("recepten_data", `tenant_id=eq.${tenantId}`, { data, updated_at: new Date().toISOString() });
    } else {
      await sb.insert("recepten_data", { tenant_id: tenantId, data, updated_at: new Date().toISOString() });
    }
  } catch (e) { console.warn("[Recepten] save failed:", e); }
}

function initState() {
  const saved = loadLocal();
  if (saved?.recepten) return saved;
  return { recepten: [], categorieen: ["Vlees","Vis","Soepen","Sauzen","Dessert","Overig"] };
}

// ─────────────────────────────────────────────────────────────────────────────
// RECEPTEN MODULE
// ─────────────────────────────────────────────────────────────────────────────
export default function ReceptenModule({ sb, tenantId, activeTab, onTabChange, openReceptId, onOpenReceptHandled, supabaseClient, userRole }) {
  const { requireBeheer } = useBeheerPin();
  const magBeheren = userRole !== "medewerker"; // beheerder + manager mogen aanmaken/bewerken/verwijderen
  const [state, setState] = useState(initState);
  const [selId, setSelId] = useState(null);        // geopend recept
  const [bedrijf, setBedrijf] = useState({ naam: "", logo: "" }); // voor printkop
  const [thtNorm, setThtNorm] = useState(3); // wettelijke THT-norm (dagen), uit Instellingen

  // Extern aansturen: open direct een specifiek recept
  useEffect(() => {
    if (openReceptId) {
      setSelId(openReceptId);
      onOpenReceptHandled?.();
    }
  }, [openReceptId]);
  const [synced, setSynced] = useState(false);
  const [syncStatus, setSyncStatus] = useState("local");
  const pollRef = useRef(null);
  const lastUpdatedAtRef = useRef(null);

  const tab = activeTab || "recepten";
  const { recepten, categorieen } = state;

  // ── 1. Load van Supabase bij mount ───────────────────────────────────────────
  // Opslaan (via slaOp) gaat PAS naar Supabase na een GESLAAGDE load. sb.select gooit
  // geen fout maar geeft [] bij mislukken — daarom: geeft de server NIETS terug terwijl
  // er lokaal wél recepten zijn, dan behandelen we dat als een mislukte load: niet
  // syncen, opnieuw proberen. Zo overschrijft een lege lijst nooit de echte recepten.
  useEffect(() => {
    if (!sb || !tenantId) return;
    let cancelled = false;
    let pogingen = 0;
    const probeer = async () => {
      setSyncStatus("syncing");
      const rows = await sb.select("recepten_data", `tenant_id=eq.${tenantId}&select=data,updated_at&limit=1`);
      if (cancelled) return;
      const serverData = rows?.length ? rows[0]?.data : null;
      if (serverData?.recepten) {
        // Server gaf een bruikbare rij terug (ook een lege lijst telt) → toepassen + sync aan
        lastUpdatedAtRef.current = rows[0].updated_at;
        setState(serverData);
        saveLocal(serverData);
        setSyncStatus("synced");
        setSynced(true);
        return;
      }
      // Server gaf niets terug. Lokaal wél recepten? → verdacht (mislukte load).
      const lokaal = loadLocal();
      if (lokaal?.recepten?.length) {
        console.warn("[Recepten] load gaf niets terug terwijl er lokaal data is; opslaan uitgesteld, opnieuw proberen (poging " + (pogingen + 1) + ").");
        setSyncStatus("local");
        if (!cancelled && pogingen < 5) { pogingen++; setTimeout(probeer, 3000); }
        return;
      }
      // Geen serverdata én geen lokale data → echt (nog) leeg → sync mag aan.
      setSyncStatus("synced");
      setSynced(true);
    };
    probeer();
    return () => { cancelled = true; };
  }, [sb, tenantId]);

  // ── Bedrijfsnaam + logo ophalen (voor de printkop van recepten) ──────────────
  useEffect(() => {
    if (!sb || !tenantId) return;
    let actief = true;
    (async () => {
      try {
        const rows = await sb.select("app_config", `tenant_id=eq.${tenantId}&select=bedrijfsnaam,logo_url,tht_norm_dagen&limit=1`);
        if (actief && rows?.[0]) {
          setBedrijf({ naam: rows[0].bedrijfsnaam || "", logo: rows[0].logo_url || "" });
          if (rows[0].tht_norm_dagen != null) setThtNorm(Number(rows[0].tht_norm_dagen) || 3);
        }
      } catch (e) { /* stil — printkop valt terug op alleen receptnaam */ }
    })();
    return () => { actief = false; };
  }, [sb, tenantId]);

  // ── 3. Poll elke 5 seconden voor real-time sync ──────────────────────────────
  useEffect(() => {
    if (!sb || !tenantId) return;
    const poll = async () => {
      if (!synced) return;
      try {
        const rows = await sb.select("recepten_data", `tenant_id=eq.${tenantId}&select=data,updated_at&limit=1`);
        if (!rows?.length || !rows[0].updated_at) return;
        if (rows[0].updated_at === lastUpdatedAtRef.current) return;
        lastUpdatedAtRef.current = rows[0].updated_at;
        if (rows[0].data) { setState(rows[0].data); saveLocal(rows[0].data); }
      } catch {}
    };
    pollRef.current = setInterval(poll, 5000);
    return () => clearInterval(pollRef.current);
  }, [sb, tenantId, synced]);

  // ── 4. Supabase Realtime WebSocket voor instant sync ────────────────────────
  useEffect(() => {
    if (!supabaseClient || !tenantId || !synced) return;
    const channel = supabaseClient
      .channel(`recepten_data_${tenantId}`)
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "recepten_data",
        filter: `tenant_id=eq.${tenantId}`,
      }, (payload) => {
        const remote = payload.new?.data;
        if (remote && remote.recepten) {
          setState(remote);
          saveLocal(remote);
          lastUpdatedAtRef.current = payload.new?.updated_at || null;
        }
      })
      .subscribe();
    return () => supabaseClient.removeChannel(channel);
  }, [supabaseClient, tenantId, synced]);

  // ── CRUD helpers ─────────────────────────────────────────────────────────────
  const slaOp = useCallback(async (nieuweState) => {
    // 1. Eerst lokaal opslaan
    saveLocal(nieuweState);
    setState(nieuweState);
    // 2. Naar Supabase — ALLEEN na een geslaagde load (synced). Schrijven vóór een
    //    geslaagde load zou de echte serverlijst kunnen overschrijven.
    if (sb && tenantId && synced) {
      try {
        await sbSave(sb, tenantId, nieuweState);
        // Update lastUpdatedAt zodat poll/Realtime eigen echo herkent
        const rows = await sb.select("recepten_data", `tenant_id=eq.${tenantId}&select=updated_at&limit=1`);
        if (rows?.length) lastUpdatedAtRef.current = rows[0].updated_at;
      } catch(e) { console.warn("[Recepten] save failed:", e); }
    }
  }, [sb, tenantId, synced]);

  // ── Onthouden artikellijst (gedeeld met MEP/whiteboard) ──────────────────────
  const [artikelen, setArtikelen] = useState([]);
  const artikelenRef = useRef([]);
  useEffect(() => { artikelenRef.current = artikelen; }, [artikelen]);

  useEffect(() => {
    if (!sb || !tenantId) return;
    let actief = true;
    (async () => {
      try {
        const rows = await sb.select("artikelen", `tenant_id=eq.${tenantId}&select=data&limit=1`);
        if (actief && Array.isArray(rows?.[0]?.data)) setArtikelen(rows[0].data);
      } catch { /* stil — suggesties beginnen dan leeg */ }
    })();
    return () => { actief = false; };
  }, [sb, tenantId]);

  // Voeg de aangevinkte bestelartikelen uit een recept toe aan de onthouden lijst
  const mergeArtikelen = useCallback(async (recept) => {
    const namen = (recept?.ingredienten || [])
      .filter(i => i.bestel && i.naam?.trim())
      .map(i => i.naam.trim());
    if (!namen.length) return;
    const huidig = artikelenRef.current || [];
    const aanwezig = new Set(huidig.map(a => a.toLowerCase()));
    const nieuw = [];
    namen.forEach(n => { if (!aanwezig.has(n.toLowerCase())) { aanwezig.add(n.toLowerCase()); nieuw.push(n); } });
    if (!nieuw.length) return;
    const lijst = [...huidig, ...nieuw].sort((a, b) => a.localeCompare(b, "nl"));
    setArtikelen(lijst);
    artikelenRef.current = lijst;
    if (sb && tenantId) {
      try {
        const exists = await sb.select("artikelen", `tenant_id=eq.${tenantId}&select=tenant_id&limit=1`);
        if (exists?.length) await sb.update("artikelen", `tenant_id=eq.${tenantId}`, { data: lijst, updated_at: new Date().toISOString() });
        else await sb.insert("artikelen", { tenant_id: tenantId, data: lijst, updated_at: new Date().toISOString() });
      } catch (e) { console.warn("[Artikelen] save failed:", e); }
    }
  }, [sb, tenantId]);

  // ── CRUD ────────────────────────────────────────────────────────────────────
  const slaRecept = async (recept) => {
    if (!(await requireBeheer("een recept te wijzigen"))) return;
    const huidig = { ...state };
    const nieuweRecepten = huidig.recepten.find(r => r.id === recept.id)
      ? huidig.recepten.map(r => r.id === recept.id ? recept : r)
      : [...huidig.recepten, recept];
    slaOp({ ...huidig, recepten: nieuweRecepten });
    mergeArtikelen(recept);
  };
  const verwijderRecept = async (id) => {
    if (!(await requireBeheer("een recept te verwijderen"))) return;
    await slaOp({ ...state, recepten: state.recepten.filter(r => r.id !== id) });
    // Only remove the object after the server confirms the recipe is gone.
    if (supabaseClient && tenantId && synced) {
      try {
        const { data, error } = await supabaseClient.from("recepten_data").select("data").eq("tenant_id", tenantId).single();
        if (!error && data?.data?.recepten && !data.data.recepten.some(r => r.id === id)) {
          const { error: photoError } = await supabaseClient.storage.from(PHOTO_BUCKET).remove([photoPath(tenantId, id)]);
          if (photoError) alert("Het recept is verwijderd, maar de foto kon niet worden opgeruimd. Neem contact op met KitchenControl.");
        }
      } catch { alert("De receptfoto kon niet worden opgeruimd. Neem contact op met KitchenControl."); }
    }
    if (selId === id) setSelId(null);
  };
  const archiveerRecept = async (id, archived) => {
    if (!(await requireBeheer("een recept te archiveren"))) return;
    slaOp({ ...state, recepten: state.recepten.map(r => r.id === id ? { ...r, archived } : r) });
  };

  const [pendingSelId, setPendingSelId] = useState(null);

  useEffect(() => {
    if (pendingSelId && state.recepten.find(r => r.id === pendingSelId)) {
      setSelId(pendingSelId);
      setPendingSelId(null);
    }
  }, [state.recepten, pendingSelId]);

  // Navigeer naar 'recepten' na opslaan via paste/nieuw
  const handleSaveAndGo = async (recept) => {
    if (!(await requireBeheer("een recept op te slaan"))) return;
    const huidig = loadLocal() || { recepten: [], categorieen };
    const bestaatAl = huidig.recepten?.find(r => r.id === recept.id);
    const nieuweRecepten = bestaatAl
      ? huidig.recepten.map(r => r.id === recept.id ? recept : r)
      : [...(huidig.recepten || []), recept];
    const nieuweState = { ...huidig, recepten: nieuweRecepten };
    slaOp(nieuweState);
    mergeArtikelen(recept);
    setPendingSelId(recept.id);
    onTabChange?.("recepten");
  };

  return (
    <RecipePhotoContext.Provider value={{ client: supabaseClient, tenant: tenantId, canEdit: magBeheren, authorize: requireBeheer }}>
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: C.bg, fontFamily: "system-ui,-apple-system,sans-serif", fontSize: 13, color: C.txt, overflow: "hidden" }}>

      <style>{RECIPE_STYLES}</style>
      {/* ── Header ── */}
      <header style={{ background: C.hdr, borderBottom: `1px solid ${C.hdrBdr}`, height: 52, display: "flex", alignItems: "center", padding: "0 18px", gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>
          {tab === "paste" ? "🍽 Recept plakken" : tab === "nieuw" ? "➕ Nieuw recept" : tab === "categorieen" ? "⚙️ Categorieën" : "📖 Recepten"}
        </span>
        <div style={{ flex: 1 }} />
        <Whiteboard sb={sb} tenantId={tenantId} supabaseClient={supabaseClient} />
        {sb && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: syncStatus === "syncing" ? "#FCD34D" : "rgba(255,255,255,.45)" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: syncStatus === "syncing" ? "#FCD34D" : "#4ADE80" }} />
            {syncStatus === "syncing" ? "synchroniseren…" : "gesynchroniseerd"}
          </div>
        )}
      </header>

      {/* ── Content ── */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex" }}>
        {tab === "recepten" && (
          <ReceptenLijst
            recepten={recepten}
            categorieen={categorieen}
            selId={selId}
            setSelId={setSelId}
            onNew={async () => { if (await requireBeheer("een recept toe te voegen")) onTabChange?.("nieuw"); }}
            onSave={slaRecept}
            onDelete={verwijderRecept}
            onArchive={archiveerRecept}
            bedrijf={bedrijf}
            thtNorm={thtNorm}
            magBeheren={magBeheren}
            artikelen={artikelen}
          />
        )}
        {tab === "nieuw" && (
          <ReceptForm
            recept={null}
            categorieen={categorieen}
            onSave={handleSaveAndGo}
            onCancel={() => onTabChange?.("recepten")}
            thtNorm={thtNorm}
            artikelen={artikelen}
          />
        )}
        {tab === "paste" && (
          <PasteView
            categorieen={categorieen}
            onSave={handleSaveAndGo}
            onCancel={() => onTabChange?.("recepten")}
            thtNorm={thtNorm}
            artikelen={artikelen}
          />
        )}
        {tab === "categorieen" && (
          <CategorieenView
            categorieen={categorieen}
            onUpdate={(cats) => slaOp({ ...state, categorieen: cats })}
          />
        )}
      </div>
    </div>
    </RecipePhotoContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RECEPTEN LIJST
// ─────────────────────────────────────────────────────────────────────────────
export function ReceptenLijst({ recepten, categorieen, selId, setSelId, onNew, onSave, onDelete, onArchive, bedrijf, thtNorm, magBeheren, artikelen = [] }) {
  const [zoek, setZoek] = useState("");
  const [zoekIn, setZoekIn] = useState("recept");
  const [catFilter, setCatFilter] = useState("alle");
  const [extraFilter, setExtraFilter] = useState("alles");
  const [toonArchief, setToonArchief] = useState(false);
  const [smal, setSmal] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const query = window.matchMedia("(max-width: 768px)");
    const update = () => setSmal(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  const heeftAllergenen = (r) => (r.allergenen || []).length > 0;
  const heeftIngredient = (r, q) => (r.ingredienten || []).some(i => (i.naam || "").toLowerCase().includes(q));
  const heeftBestel = (r) => (r.ingredienten || []).some(i => i.bestel);

  const zichtbaar = recepten
    .filter(r => toonArchief ? r.archived : !r.archived)
    .filter(r => catFilter === "alle" || r.categorie === catFilter)
    .filter(r => {
      if (extraFilter === "met-allergenen") return heeftAllergenen(r);
      if (extraFilter === "zonder-allergenen") return !heeftAllergenen(r);
      if (extraFilter === "met-bestel") return heeftBestel(r);
      if (extraFilter === "veel-ingredienten") return (r.ingredienten || []).length > 10;
      return true;
    })
    .filter(r => {
      const q = zoek.trim().toLowerCase();
      if (!q) return true;
      return zoekIn === "ingredient"
        ? heeftIngredient(r, q)
        : r.naam.toLowerCase().includes(q);
    })
    .sort((a, b) => a.naam.localeCompare(b.naam, "nl"));

  const geselecteerd = selId ? recepten.find(r => r.id === selId) : null;

  // Mobiel: toon detail fullscreen als geselecteerd
  if (smal && geselecteerd) {
    return (
      <div className="kc-recipe-browser" style={{ display: "flex", flex: 1, minWidth: 0, overflow: "hidden" }}>
        <div style={{ flex: 1, overflow: "hidden" }}>
          <ReceptDetail key={geselecteerd.id}
            recept={geselecteerd}
            categorieen={categorieen}
            onSave={onSave}
            onDelete={onDelete}
            onArchive={onArchive}
            onClose={() => setSelId(null)}
            bedrijf={bedrijf}
            thtNorm={thtNorm}
            magBeheren={magBeheren}
            artikelen={artikelen}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="kc-recipe-browser" style={{ display: "flex", flex: 1, minWidth: 0, overflow: "hidden" }}>
      {/* Linker paneel — lijst */}
      <div style={{ width: !smal ? 350 : "100%", minWidth: smal ? 0 : 280, borderRight: !smal ? `1px solid ${C.bdr}` : "none", display: "flex", flexDirection: "column", overflow: "hidden", transition: "width .2s" }}>
        {/* Zoek + filter */}
        <div className="kc-recipe-list-title"><h2>Recepten</h2></div>
        <div style={{ padding: "12px 14px", borderBottom: `1px solid ${C.bdr}`, display: "flex", flexDirection: "column", gap: 8, background: C.bg2 }}>
          {/* KC_UI_V2_RECEPT_TOEVOEGEN: één duidelijke primaire actie, naast zoeken. */}
          <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
              <Search size={13} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: C.txt3, pointerEvents: "none" }} />
              <input aria-label="Zoek recepten of ingrediënten" style={{ ...inp, paddingLeft: 28, fontSize: 15, minHeight: 44, height: "100%" }} placeholder={zoekIn === "ingredient" ? "Zoeken op ingrediënt..." : "Zoeken op recept..."} value={zoek} onChange={e => setZoek(e.target.value)} />
            </div>
            {magBeheren && (
              <button type="button" onClick={onNew}
                style={{ ...btnStyle(C.pri, "#fff", C.pri), flexShrink: 0, whiteSpace: "nowrap", padding: "7px 12px" }}>
                <Plus size={16} /> Recept
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "inline-flex", border: `1px solid ${C.bdr}`, borderRadius: 8, overflow: "hidden", background: C.bg3 }}>
              {[
                ["recept", "Recept"],
                ["ingredient", "Ingrediënt"],
              ].map(([id, label]) => (
                <button key={id} onClick={() => setZoekIn(id)}
                  style={{ border: "none", borderRight: id === "recept" ? `1px solid ${C.bdr}` : "none", background: zoekIn === id ? C.pri : "transparent", color: zoekIn === id ? "#fff" : C.txt2, padding: "5px 9px", fontFamily: "inherit", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  {label}
                </button>
              ))}
            </div>
            <select aria-label="Extra filters" value={extraFilter} onChange={e => setExtraFilter(e.target.value)}
              style={{ ...inp, width: "auto", minWidth: 150, padding: "5px 9px", fontSize: 14, color: C.txt2, background: C.bg2 }}>
              <option value="alles">Extra filters</option>
              <option value="met-allergenen">Met allergenen</option>
              <option value="zonder-allergenen">Zonder allergenen</option>
              <option value="met-bestel">Met bestelartikel</option>
              <option value="veel-ingredienten">Meer dan 10 ingrediënten</option>
            </select>
          </div>
          <select aria-label="Categorie" value={catFilter} onChange={e => setCatFilter(e.target.value)} style={{ ...inp, minHeight: 44, fontSize: 15 }}>
            <option value="alle">Alle categorieën</option>
            {categorieen.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>

        {/* Lijst */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {zichtbaar.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: C.txt3 }}>
              <BookOpen size={36} strokeWidth={1} style={{ marginBottom: 12, display: "block", margin: "0 auto 12px" }} />
              {zoek ? (
                <p style={{ fontSize: 13 }}>Geen recepten gevonden.</p>
              ) : (
                <p style={{ fontSize: 13 }}>
                  Nog geen recepten. Maak je eerste <button type="button" onClick={onNew} style={{ background: "none", border: "none", padding: 0, margin: 0, color: C.pri, font: "inherit", fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}>nieuw recept</button>.
                </p>
              )}
            </div>
          ) : (
            zichtbaar.map(r => {
              const allergenenAantal = (r.allergenen || []).filter(a => !GLUTEN_SUB.includes(a) && !NOTEN_SUB.includes(a)).length;
              const bestelAantal = (r.ingredienten || []).filter(i => i.bestel).length;
              const ingredientHit = zoekIn === "ingredient" && zoek.trim()
                ? (r.ingredienten || []).find(i => (i.naam || "").toLowerCase().includes(zoek.trim().toLowerCase()))
                : null;
              return (
              <button type="button" className="kc-recipe-row" aria-pressed={selId === r.id} key={r.id} onClick={() => setSelId(r.id)}
                style={{ width: "100%", textAlign: "left", font: "inherit", border: 0, padding: "18px 16px", borderBottom: `1px solid ${C.bdr}`, cursor: "pointer", background: selId === r.id ? "#E8F5EE" : C.bg2, borderLeft: `3px solid ${selId === r.id ? "#08664F" : "transparent"}`, transition: "background .1s" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <RecipeThumbnail recipeId={r.id} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 750, color: selId === r.id ? "#08664F" : C.txt, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.naam}</div>
                    <div style={{ display: "flex", gap: 7, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                      {r.categorie && <span style={{ fontSize: 13, color: C.txt2, fontWeight: 650 }}>{r.categorie}</span>}
                      {r.ingredienten?.length > 0 && <span style={{ fontSize: 13, color: C.txt3 }}>{r.ingredienten.length} ingrediënten</span>}
                      {allergenenAantal > 0 && (
                        <span style={{ fontSize: 13, color: "#B91C1C", display: "flex", alignItems: "center", gap: 3, fontWeight: 650 }}>
                          <TriangleAlert size={10} />{allergenenAantal} allergen{allergenenAantal !== 1 ? "en" : ""}
                        </span>
                      )}
                      {bestelAantal > 0 && <span style={{ fontSize: 13, color: C.pri, background: C.priBg, border: `1px solid ${C.priBd}`, borderRadius: 999, padding: "1px 7px", fontWeight: 700 }}>Bestel {bestelAantal}</span>}
                    </div>
                    {ingredientHit && (
                      <div style={{ fontSize: 13, color: C.txt3, marginTop: 5 }}>
                        Ingrediënt: <span style={{ color: C.txt2, fontWeight: 650 }}>{ingredientHit.naam}</span>
                      </div>
                    )}
                  </div>
                  {!smal && <ChevronRight size={16} color={C.txt3} style={{ marginTop: 2, flexShrink: 0 }} />}
                </div>
              </button>
              );
            })
          )}
        </div>

        {/* Archief toggle */}
        <div style={{ padding: "8px 14px", borderTop: `1px solid ${C.bdr}`, background: C.bg2, display: "flex", justifyContent: "space-between", gap: 10 }}>
          <span style={{ fontSize: 14, color: C.txt3 }}>{zichtbaar.length} recept{zichtbaar.length !== 1 ? "en" : ""}</span>
          <button onClick={() => setToonArchief(t => !t)}
            style={{ fontSize: 14, color: C.txt3, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            {toonArchief ? "Actieve recepten" : `Archief (${recepten.filter(r => r.archived).length})`}
          </button>
        </div>
      </div>

      {/* Rechter paneel — snelle preview/detail */}
      {!smal && (
        <div style={{ flex: 1, minWidth: 0, overflow: "hidden", background: "#EFF5F2" }}>
          {geselecteerd ? (
            <ReceptDetail key={geselecteerd.id}
              recept={geselecteerd}
              categorieen={categorieen}
              onSave={onSave}
              onDelete={onDelete}
              onArchive={onArchive}
              onClose={() => setSelId(null)}
              bedrijf={bedrijf}
              thtNorm={thtNorm}
              magBeheren={magBeheren}
              artikelen={artikelen}
            />
          ) : (
            <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: C.txt3, padding: 24 }}>
              <div style={{ textAlign: "center", maxWidth: 320 }}>
                <BookOpen size={42} strokeWidth={1.2} style={{ marginBottom: 12 }} />
                <div style={{ fontSize: 16, fontWeight: 800, color: C.txt2, marginBottom: 5 }}>Kies een recept</div>
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>Bekijk ingrediënten, allergenen en bereiding direct naast de lijst.</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RECEPT DETAIL (lezen + bewerken)
// ─────────────────────────────────────────────────────────────────────────────
export function ReceptDetail({ recept, categorieen, onSave, onDelete, onArchive, onClose, bedrijf, thtNorm = 3, magBeheren = true, artikelen = [] }) {
  const [editing, setEditing] = useState(false);
  const [factor, setFactor] = useState(1);
  const [factorInput, setFactorInput] = useState("1");
  const [kitchen, setKitchen] = useState(false);
  const [wakeStatus, setWakeStatus] = useState("idle");
  const dialogRef = useRef(null);
  const kitchenButtonRef = useRef(null);
  const validFactor = Number.isFinite(Number(factorInput)) && Number(factorInput) > 0 && Number(factorInput) <= 1000;
  const chooseFactor = (value) => { setFactor(value); setFactorInput(String(value)); };
  useEffect(() => {
    if (!kitchen) return;
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      kitchenButtonRef.current?.focus();
    };
  }, [kitchen]);
  useEffect(() => {
    if (!kitchen) return;
    let stopped = false;
    let sentinel = null;
    let pending = false;
    const request = async () => {
      if (stopped || pending || document.visibilityState !== "visible" || (sentinel && !sentinel.released)) return;
      if (!navigator.wakeLock?.request) { setWakeStatus("unsupported"); return; }
      pending = true;
      setWakeStatus("requesting");
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (stopped) { await lock.release(); return; }
        sentinel = lock;
        setWakeStatus("active");
        lock.addEventListener("release", () => { if (!stopped) setWakeStatus("released"); });
      } catch { if (!stopped) setWakeStatus("unavailable"); }
      finally { pending = false; }
    };
    request();
    document.addEventListener("visibilitychange", request);
    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", request);
      sentinel?.release().catch(() => {});
    };
  }, [kitchen]);
  const steps = (recept.bereiding || "").split(/\n+/).map(line => line.trim()).filter(Boolean);
  const hasUnscaled = factor !== 1 && (recept.ingredienten || []).some(ing => String(ing.hoeveelheid || "").trim() && !recipeQuantity(ing.hoeveelheid, factor).scaled);


  // ── Recept printen: open een net A4-vel in een nieuw venster en print ────────
  const printRecept = () => {
    const esc = (s) => String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

    const hoofd = (recept.allergenen || []).filter(a => !GLUTEN_SUB.includes(a) && !NOTEN_SUB.includes(a));
    const glutenSubs = (recept.allergenen || []).filter(a => GLUTEN_SUB.includes(a));
    const notenSubs = (recept.allergenen || []).filter(a => NOTEN_SUB.includes(a));

    const allergieRegels = [];
    if (hoofd.length) allergieRegels.push(hoofd.map(esc).join(" · "));
    if (glutenSubs.length) allergieRegels.push("<b>Gluten:</b> " + glutenSubs.map(esc).join(", "));
    if (notenSubs.length) allergieRegels.push("<b>Noten:</b> " + notenSubs.map(esc).join(", "));

    const ingredientenHtml = (recept.ingredienten || []).map(ing =>
      `<tr><td class="ing-naam">${esc(ing.naam)}</td><td class="ing-hoev">${esc(recipeQuantity(ing.hoeveelheid, factor).text)} ${esc(ing.eenheid)}</td></tr>`
    ).join("");

    const bewaarRegels = [];
    if (recept.bewaaradvies) bewaarRegels.push(esc(recept.bewaaradvies));
    if (recept.thtDagen) bewaarRegels.push(`Houdbaar: ${esc(recept.thtDagen)} dagen na bereiding`);

    const vandaag = new Date().toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
    const naamBedrijf = (bedrijf && bedrijf.naam) || "";
    const logo = (bedrijf && bedrijf.logo) || "";

    const html = `<!doctype html><html lang="nl"><head><meta charset="utf-8">
<title>${esc(recept.naam)} — recept</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #1A1A1A; font-size: 13px; line-height: 1.5; padding: 26mm 22mm; }
  .kop { display: flex; align-items: center; gap: 12px; padding-bottom: 12px;
    border-bottom: 2px solid #D97706; margin-bottom: 26px; }
  .kop img { max-height: 42px; max-width: 150px; object-fit: contain; }
  .kop .naam { font-size: 15px; font-weight: 700; color: #0F172A; letter-spacing: .2px; }
  .eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #D97706; }
  h1 { font-family: Georgia, "Times New Roman", serif; font-size: 30px; font-weight: 600;
    color: #0F172A; margin: 4px 0 2px; letter-spacing: -.4px; }
  .cat { font-size: 12px; color: #64748B; margin-bottom: 18px; }

  .allerg { border: 1px solid #F0B5B5; border-radius: 6px; padding: 10px 14px; margin-bottom: 16px; }
  .allerg .label { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: #B91C1C; margin-bottom: 5px; }
  .allerg .regel { font-size: 12.5px; color: #1A1A1A; }
  .bewaar { font-size: 12px; color: #475569; background: #FAFAF8; border: 1px solid #E5E5E0;
    border-radius: 6px; padding: 8px 12px; margin-bottom: 24px; }
  .sectie { font-family: Georgia, serif; font-size: 16px; font-weight: 600; color: #0F172A;
    margin: 0 0 8px; padding-bottom: 5px; border-bottom: 1px solid #E2E8F0; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 26px; }
  td { padding: 5px 0; border-bottom: 1px solid #EEE; vertical-align: top; }
  .ing-naam { color: #1A1A1A; }
  .ing-hoev { text-align: right; color: #475569; white-space: nowrap; padding-left: 16px; }
  .bereiding { white-space: pre-wrap; font-size: 13px; line-height: 1.7; color: #1A1A1A; margin-bottom: 30px; }
  .voet { margin-top: 30px; padding-top: 12px; border-top: 1px solid #E2E8F0;
    font-size: 10.5px; color: #94A3B8; display: flex; justify-content: space-between; }
  @media print { body { padding: 16mm; } .allerg { border-color: #B91C1C; } }
  @page { margin: 0; }
</style></head><body>
  ${(logo || naamBedrijf) ? `<div class="kop">${logo ? `<img src="${esc(logo)}" alt="">` : ""}${naamBedrijf ? `<span class="naam">${esc(naamBedrijf)}</span>` : ""}</div>` : ""}
  <div class="eyebrow">Recept · ${esc(factor)}× basisrecept</div>
  <h1>${esc(recept.naam)}</h1>
  ${recept.categorie ? `<div class="cat">${esc(recept.categorie)}</div>` : ""}
  ${allergieRegels.length ? `<div class="allerg"><div class="label">⚠ Allergenen</div>${allergieRegels.map(r => `<div class="regel">${r}</div>`).join("")}</div>` : ""}
  ${bewaarRegels.length ? `<div class="bewaar">🌡 ${bewaarRegels.join(" &nbsp;·&nbsp; ")}</div>` : ""}
  ${hasUnscaled ? `<p>Hoeveelheden in woorden zijn niet omgerekend. Controleer deze zelf.</p>` : ""}
  ${ingredientenHtml ? `<div class="sectie">Ingrediënten</div><table>${ingredientenHtml}</table>` : ""}
  ${recept.bereiding ? `<div class="sectie">Bereiding</div><div class="bereiding">${esc(recept.bereiding)}</div>` : ""}
  <div class="voet"><span>Geprint op ${vandaag}</span><span>${esc(naamBedrijf)}</span></div>
  <script>window.onload=function(){setTimeout(function(){window.print();},350);};window.onafterprint=function(){window.close();};<\/script>
</body></html>`;

    const w = window.open("", "_blank", "width=820,height=1040");
    if (!w) { alert("Sta pop-ups toe voor app.kitchencontrol.nl om recepten te kunnen printen."); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
  };

  if (editing) {
    return (
      <ReceptForm
        recept={recept}
        categorieen={categorieen}
        onSave={(r) => { onSave(r); setEditing(false); }}
        onCancel={() => setEditing(false)}
        thtNorm={thtNorm}
        artikelen={artikelen}
      />
    );
  }

  const view = (
    <div className={`kc-recipe-sheet${kitchen ? " kc-kitchen" : ""}`}>
      <div className="kc-recipe-heading">
        <div className="kc-recipe-heading-copy"><h1>{recept.naam}</h1>{recept.categorie && <p>{recept.categorie}</p>}</div>
        <div className="kc-recipe-actions">
          {kitchen ? <button type="button" autoFocus onClick={() => setKitchen(false)}><ArrowLeft size={18} />Terug naar recepten</button> :
            <button type="button" ref={kitchenButtonRef} onClick={() => setKitchen(true)}><ChefHat size={18} />Keukenstand</button>}
          {!kitchen && magBeheren && <button type="button" className="kc-recipe-edit" onClick={() => setEditing(true)}><Pencil size={17} />Bewerken</button>}
          <button type="button" onClick={printRecept}><Printer size={17} />Printen</button>
          {!kitchen && magBeheren && <details className="kc-recipe-more"><summary aria-label="Meer receptacties"><MoreHorizontal size={22} /></summary><div>
            <button type="button" onClick={() => { if (confirm(`"${recept.naam}" ${recept.archived ? "terugzetten" : "archiveren"}?`)) { onArchive(recept.id, !recept.archived); onClose(); } }}>{recept.archived ? "Terugzetten" : "Archiveren"}</button>
            <button type="button" className="kc-recipe-delete" onClick={() => { if (confirm(`"${recept.naam}" definitief verwijderen?`)) { onDelete(recept.id); onClose(); } }}><Trash2 size={16} />Verwijderen</button>
          </div></details>}
          {!kitchen && <button type="button" aria-label="Recept sluiten" onClick={onClose}><X size={18} /></button>}
        </div>
      </div>
      {kitchen && <p className="kc-wake-status" role="status">{wakeStatus === "active" ? "Scherm blijft aan zolang deze pagina zichtbaar is." : wakeStatus === "requesting" ? "Scherm-aanfunctie wordt gestart…" : "Scherm-aanfunctie niet actief. Controleer de slaapstand van je apparaat."}</p>}
      <div className="kc-recipe-scroll">
        <RecipePhoto recipeId={recept.id} name={recept.naam} readOnly={kitchen || !magBeheren} />
        <div className="kc-recipe-notices">
          {!!recept.allergenen?.length && <div className="kc-recipe-allergens"><TriangleAlert size={20} /><strong>Allergenen:</strong><div>{recept.allergenen.map(a => <span key={a}>{a}</span>)}</div></div>}
          {recept.bewaaradvies && <div className="kc-recipe-storage"><Thermometer size={20} />{recept.bewaaradvies}</div>}
        </div>
        <div className="kc-recipe-columns">
          <section>
            <div className="kc-recipe-factor"><span>Receptfactor</span><div className="kc-factor-options">{[0.5, 1, 2, 4].map(value => <button type="button" key={value} aria-pressed={factor === value} onClick={() => chooseFactor(value)}>{value === 0.5 ? "½" : value}×</button>)}</div><label>Eigen factor<input aria-label="Eigen receptfactor" type="number" min="0.01" max="1000" step="any" value={factorInput} aria-invalid={!validFactor} onChange={e => { const value = e.target.value; setFactorInput(value); const number = Number(value); if (value && Number.isFinite(number) && number > 0 && number <= 1000) setFactor(number); }} onBlur={() => { if (!validFactor) setFactorInput(String(factor)); }} /></label></div>
            {!validFactor && <p className="kc-recipe-note" role="alert">Vul een factor groter dan 0 en maximaal 1000 in.</p>}
            <h2>Ingrediënten</h2>
            {factor !== 1 && <p className="kc-recipe-note">{factor.toLocaleString("nl-NL")}× het basisrecept. Alleen de hoeveelheden hieronder en op de afdruk zijn aangepast.</p>}
            {hasUnscaled && <p className="kc-recipe-note">Hoeveelheden in woorden blijven ongewijzigd. Controleer deze zelf.</p>}
            {recept.ingredienten?.length ? <ul className="kc-recipe-ingredients">{recept.ingredienten.map((ing, i) => <li key={i}><span>{ing.naam}</span><span>{recipeQuantity(ing.hoeveelheid, factor).text} {ing.eenheid}</span></li>)}</ul> : <p>Geen ingrediënten ingevuld.</p>}
          </section>
          <section><h2>Bereiding</h2>{steps.length ? <ol className="kc-recipe-steps">{steps.map((step, i) => <li key={i}><span aria-hidden="true">{i + 1}</span><p>{step.replace(/^\d+[.)]\s+/, "")}</p></li>)}</ol> : <p>Nog geen bereidingswijze ingevuld.</p>}</section>
        </div>
      </div>
    </div>
  );
  return kitchen ? createPortal(<dialog className="kc-kitchen-dialog" ref={dialogRef} aria-label={`Keukenstand: ${recept.naam}`} onCancel={e => { e.preventDefault(); setKitchen(false); }}>{view}</dialog>, document.body) : view;
}

// ─────────────────────────────────────────────────────────────────────────────
// RECEPT FORM (nieuw + bewerken)
// ─────────────────────────────────────────────────────────────────────────────
function ReceptForm({ recept, categorieen, onSave, onCancel, thtNorm = 3, artikelen = [] }) {
  const leeg = { id: uid(), naam: "", categorie: "", ingredienten: [], bereiding: "", allergenen: [], bewaaradvies: "", thtDagen: thtNorm, foto_url: "", archived: false };
  const [form, setForm] = useState(recept ? { ...recept } : leeg);
  const formRef = useRef(form); // altijd de laatste form waarde
  useEffect(() => { formRef.current = form; }, [form]);
  const [nieuwIng, setNieuwIng] = useState({ naam: "", hoeveelheid: "", eenheid: "kg" });
  const [ingFout, setIngFout] = useState(false);
  const [editIngId, setEditIngId] = useState(null);  // id van ingrediënt in edit-modus
  const [editIngForm, setEditIngForm] = useState({ naam: "", hoeveelheid: "", eenheid: "kg" });
  const [toonSug, setToonSug] = useState(false);      // suggesties tonen onder naamveld
  const [sugIdx, setSugIdx] = useState(-1);           // gemarkeerde suggestie voor toetsenbord
  const naamRef = useRef();
  const [openSecties, setOpenSecties] = useState(() => ({
    houdbaarheid: !!recept?.bewaaradvies || recept?.thtDagen !== undefined,
    allergenen: (recept?.allergenen || []).length > 0,
  }));
  const toggleSectie = (naam) => setOpenSecties(s => ({ ...s, [naam]: !s[naam] }));
  const hoofdAllergenenAantal = (form.allergenen || []).filter(a => !GLUTEN_SUB.includes(a) && !NOTEN_SUB.includes(a)).length;
  const bestelAantal = (form.ingredienten || []).filter(i => i.bestel).length;

  // Markeer/ontmarkeer een ingrediënt als bestelartikel
  const toggleBestel = (id) => setForm(f => ({
    ...f,
    ingredienten: f.ingredienten.map(i => i.id === id ? { ...i, bestel: !i.bestel } : i)
  }));

  // Suggesties uit de onthouden artikellijst, gefilterd op wat je typt
  const sugTreffers = (() => {
    const q = nieuwIng.naam.trim().toLowerCase();
    if (!q) return [];
    return (artikelen || []).filter(a => a.toLowerCase().includes(q)).slice(0, 5);
  })();

  useEffect(() => { naamRef.current?.focus(); }, []);

  // Auto-detecteer allergenen bij ingrediënt-wijzigingen
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addIng = () => {
    if (!nieuwIng.naam.trim()) { setIngFout(true); return; }
    setForm(f => ({ ...f, ingredienten: [...(f.ingredienten || []), { id: uid(), ...nieuwIng, naam: nieuwIng.naam.trim() }] }));
    setNieuwIng({ naam: "", hoeveelheid: "", eenheid: "kg" });
    setIngFout(false);
    setToonSug(false);
    setSugIdx(-1);
  };
  const delIng = (id) => setForm(f => ({ ...f, ingredienten: f.ingredienten.filter(i => i.id !== id) }));
  const startEditIng = (ing) => { setEditIngId(ing.id); setEditIngForm({ naam: ing.naam, hoeveelheid: ing.hoeveelheid, eenheid: ing.eenheid }); };
  const saveEditIng = () => {
    if (!editIngForm.naam.trim()) return;
    setForm(f => ({ ...f, ingredienten: f.ingredienten.map(i => i.id === editIngId ? { ...i, ...editIngForm, naam: editIngForm.naam.trim() } : i) }));
    setEditIngId(null);
  };

  const toggleAllergeen = (a) => setForm(f => ({
    ...f,
    allergenen: f.allergenen.includes(a) ? f.allergenen.filter(x => x !== a) : [...f.allergenen, a]
  }));

  const opslaan = () => {
    const huidigeForm = formRef.current;
    if (!huidigeForm.naam.trim()) { naamRef.current?.focus(); return; }
    if (nieuwIng.naam.trim()) {
      const extra = { id: uid(), ...nieuwIng, naam: nieuwIng.naam.trim() };
      onSave({ ...huidigeForm, ingredienten: [...(huidigeForm.ingredienten || []), extra] });
    } else {
      onSave(huidigeForm);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0, height: "100%", overflow: "hidden" }}>
      {/* Form header */}
      <div style={{ display: "flex", alignItems: "center", padding: "12px 18px", borderBottom: `1px solid ${C.bdr}`, background: C.bg2, gap: 10, flexShrink: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: C.txt, flex: 1 }}>{recept ? "Recept bewerken" : "Nieuw recept"}</span>
        <button onClick={opslaan} style={btnStyle(C.pri, "#fff", C.pri)}><Save size={14} /> Opslaan</button>
        <button onClick={onCancel} style={{ ...btnStyle(C.bg3, C.txt2, C.bdr), padding: "7px 10px" }}><X size={14} /></button>
      </div>

      {/* Houd de secties op hun natuurlijke hoogte: ingeklapte/uitgeklapte
          panelen mogen niet krimpen en hun inhoud afsnijden. */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: 20, display: "grid", gridAutoRows: "max-content", alignContent: "start", gap: 16 }}>

        <p style={{ margin: 0, color: C.txt2 }}>Een foto toevoegen of aanpassen kan in het geopende recept, na het opslaan.</p>
        {/* Basis */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 10, padding: 14 }}>
          <Veld label="Naam *" style={{ gridColumn: "1/3" }}>
            <input ref={naamRef} style={inp} placeholder="Naam van het recept" value={form.naam} onChange={e => set("naam", e.target.value)} />
          </Veld>
          <Veld label="Categorie">
            <select style={inp} value={form.categorie} onChange={e => set("categorie", e.target.value)}>
              <option value="">Geen</option>
              {categorieen.map(c => <option key={c}>{c}</option>)}
            </select>
          </Veld>
          <div style={{ alignSelf: "end", fontSize: 12, color: C.txt3 }}>
            {form.ingredienten?.length || 0} ingrediënten · {hoofdAllergenenAantal || "geen"} allergenen · {bestelAantal} bestel
          </div>
        </div>

        <InklapSectie
          titel="Houdbaarheid"
          status={form.bewaaradvies ? form.bewaaradvies : `${form.thtDagen ?? thtNorm} dagen`}
          open={openSecties.houdbaarheid}
          onToggle={() => toggleSectie("houdbaarheid")}
        >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Veld label="Bewaaradvies">
            <select style={inp} value={form.bewaaradvies} onChange={e => set("bewaaradvies", e.target.value)}>
              {BEWAARADVIES_OPTIES.map(o => <option key={o} value={o}>{o || "— geen —"}</option>)}
            </select>
          </Veld>
          <Veld label="Bewaartermijn (dagen)">
            <input type="number" style={inp} min={0} max={365} placeholder={`bijv. ${thtNorm}`}
              value={form.thtDagen ?? ""} onChange={e => set("thtDagen", e.target.value)} />
          </Veld>
          <div style={{ alignSelf: "end", background: C.bg3, border: `1px solid ${C.bdr}`, borderRadius: 9, padding: "9px 12px", fontSize: 12, color: C.txt2, lineHeight: 1.5 }}>
            <div style={{ fontWeight: 700, color: C.txt2, marginBottom: 2 }}>Richtlijn houdbaarheid</div>
            2 dagen onder 7 °C · 3 dagen onder 4 °C
          </div>
        </div>
        </InklapSectie>

        {/* Ingrediënten */}
        <div style={{ background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 10, padding: 14 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
            <Sectie titel="Ingrediënten" />
            <span style={{ fontSize: 11.5, color: C.txt3, fontWeight: 650 }}>{form.ingredienten?.length || 0} regels</span>
          </div>
          {(form.ingredienten || []).map(ing => (
            <div key={ing.id} style={{ marginBottom: 4 }}>
              {editIngId === ing.id ? (
                // Inline edit modus
                <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 90px auto auto", gap: 6, alignItems: "center", padding: "6px 10px", background: C.priBg, borderRadius: 6, border: `1px solid ${C.priBd}` }}>
                  <input style={inp} value={editIngForm.naam} autoFocus onChange={e => setEditIngForm(f => ({ ...f, naam: e.target.value }))} onKeyDown={e => e.key === "Enter" && saveEditIng()} />
                  <input style={inp} type="number" step="0.1" value={editIngForm.hoeveelheid} onChange={e => setEditIngForm(f => ({ ...f, hoeveelheid: e.target.value }))} />
                  <select style={inp} value={editIngForm.eenheid} onChange={e => setEditIngForm(f => ({ ...f, eenheid: e.target.value }))}>
                    {UNITS.map(u => <option key={u}>{u}</option>)}
                  </select>
                  <button onClick={saveEditIng} style={{ ...btnStyle(C.pri, "#fff", C.pri), padding: "6px 10px" }}>✓</button>
                  <button onClick={() => setEditIngId(null)} style={{ ...btnStyle(C.bg3, C.txt2, C.bdr), padding: "6px 10px" }}>✕</button>
                </div>
              ) : (
                // Weergave modus
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: C.bg3, borderRadius: 6 }}>
                  <span style={{ flex: 1, fontSize: 13 }}>{ing.naam}</span>
                  <span style={{ fontSize: 12, color: C.txt2 }}>{ing.hoeveelheid} {ing.eenheid}</span>
                  <button onClick={() => toggleBestel(ing.id)} title="Markeer als bestelartikel"
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer", flex: "none",
                      border: `1px solid ${ing.bestel ? C.pri : C.bdr}`, background: ing.bestel ? "#EFF6FF" : C.bg2,
                      borderRadius: 999, padding: "3px 9px 3px 5px" }}>
                    <span style={{ width: 15, height: 15, borderRadius: 4, border: `1.5px solid ${ing.bestel ? C.pri : C.txt3}`,
                      background: ing.bestel ? C.pri : "transparent", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {ing.bestel ? <Check size={11} strokeWidth={3} /> : null}
                    </span>
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: ing.bestel ? C.pri : C.txt3, whiteSpace: "nowrap" }}>Bestelartikel</span>
                  </button>
                  <button onClick={() => startEditIng(ing)} style={{ ...btnStyle(C.bg3, C.txt2, C.bdr), padding: "4px 9px", fontSize: 11 }}>Bewerk</button>
                  <button onClick={() => delIng(ing.id)} style={{ background: "none", border: "none", cursor: "pointer", color: C.txt3, padding: 2, display: "flex" }}><Trash2 size={13} /></button>
                </div>
              )}
            </div>
          ))}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 90px auto", gap: 6, marginTop: 8 }}>
            <div style={{ position: "relative" }}>
              <input style={{ ...inp, width: "100%", ...(ingFout ? { borderColor: C.red } : {}) }} placeholder="Naam ingrediënt *"
                value={nieuwIng.naam}
                onChange={e => { setNieuwIng(f => ({ ...f, naam: e.target.value })); setIngFout(false); setToonSug(true); setSugIdx(-1); }}
                onFocus={() => setToonSug(true)}
                onBlur={() => setTimeout(() => setToonSug(false), 120)}
                onKeyDown={e => {
                  if (e.key === "ArrowDown") { e.preventDefault(); if (sugTreffers.length) { setToonSug(true); setSugIdx(i => (i + 1) % sugTreffers.length); } }
                  else if (e.key === "ArrowUp") { e.preventDefault(); if (sugTreffers.length) { setToonSug(true); setSugIdx(i => (i <= 0 ? sugTreffers.length - 1 : i - 1)); } }
                  else if (e.key === "Enter") {
                    if (toonSug && sugIdx >= 0 && sugTreffers[sugIdx]) { e.preventDefault(); setNieuwIng(f => ({ ...f, naam: sugTreffers[sugIdx] })); setToonSug(false); setSugIdx(-1); }
                    else addIng();
                  }
                  else if (e.key === "Escape") { if (toonSug && sugTreffers.length) { e.stopPropagation(); setToonSug(false); setSugIdx(-1); } }
                }} />
              {toonSug && sugTreffers.length > 0 && (
                <div style={{ position: "absolute", left: 0, right: 0, top: "calc(100% + 4px)", zIndex: 20, background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(15,23,42,.12)", overflow: "hidden" }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase", color: C.txt3, padding: "8px 12px 4px" }}>Onthouden artikelen</div>
                  {sugTreffers.map((a, i) => (
                    <div key={a} onMouseDown={() => { setNieuwIng(f => ({ ...f, naam: a })); setToonSug(false); setSugIdx(-1); }}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", cursor: "pointer", background: i === sugIdx ? C.bg3 : "transparent" }}
                      onMouseEnter={() => setSugIdx(i)}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.pri, flex: "none" }} />
                      <span style={{ flex: 1, fontSize: 13, color: C.txt }}>{a}</span>
                      <span style={{ fontSize: 10.5, color: C.pri, background: "#EFF6FF", borderRadius: 999, padding: "2px 8px", fontWeight: 600 }}>bestaand</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <input style={inp} placeholder="Hoev." type="number" step="0.1" value={nieuwIng.hoeveelheid} onChange={e => setNieuwIng(f => ({ ...f, hoeveelheid: e.target.value }))} />
            <select style={inp} value={nieuwIng.eenheid} onChange={e => setNieuwIng(f => ({ ...f, eenheid: e.target.value }))}>
              {UNITS.map(u => <option key={u}>{u}</option>)}
            </select>
            <button onClick={addIng} style={{ ...btnStyle(C.pri, "#fff", C.pri), padding: "7px 10px" }}><Plus size={15} /></button>
          </div>
        </div>

        {/* Bereiding */}
        <div style={{ background: C.bg2, border: `1px solid ${C.bdr}`, borderRadius: 10, padding: 14 }}>
          <Veld label="Bereiding">
            <textarea style={{ ...inp, minHeight: 140, resize: "vertical", lineHeight: 1.6 }} placeholder="Beschrijf de bereiding…" value={form.bereiding || ""} onChange={e => set("bereiding", e.target.value)} />
          </Veld>
        </div>

        {/* Allergenen */}
        <InklapSectie
          titel="Allergenen"
          status={hoofdAllergenenAantal ? `${hoofdAllergenenAantal} gekozen` : "geen gekozen"}
          open={openSecties.allergenen}
          onToggle={() => toggleSectie("allergenen")}
        >
        <div>

          {/* 14 hoofd-allergenen */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
            {ALLERGENEN_LIJST.map(a => {
              const actief = form.allergenen?.includes(a);
                            return (
                <button key={a} onClick={() => toggleAllergeen(a)}
                  style={{ padding: "4px 10px", fontSize: 11, fontFamily: "inherit", cursor: "pointer", borderRadius: 12, fontWeight: actief ? 700 : 400, border: `1px solid ${actief ? C.red : C.bdr}`, background: actief ? C.redBg : C.bg2, color: actief ? C.red : C.txt2, position: "relative" }}>
                  {a}
                </button>
              );
            })}
          </div>

          {/* Sub-allergenen Gluten — alleen als Gluten actief */}
          {form.allergenen?.includes("Gluten") && (
            <div style={{ padding: "10px 12px", background: C.bg3, borderRadius: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>
                Gluten — specificeer graansoort
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {GLUTEN_SUB.map(sub => {
                  const actief = form.allergenen?.includes(sub);
                                    return (
                    <button key={sub} onClick={() => toggleAllergeen(sub)}
                      style={{ padding: "3px 9px", fontSize: 11, fontFamily: "inherit", cursor: "pointer", borderRadius: 10, fontWeight: actief ? 700 : 400, border: `1px solid ${actief ? C.red : C.bdr2}`, background: actief ? C.redBg : C.bg2, color: actief ? C.red : C.txt2, position: "relative" }}>
                      {sub}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sub-allergenen Noten — alleen als Noten actief */}
          {form.allergenen?.includes("Noten") && (
            <div style={{ padding: "10px 12px", background: C.bg3, borderRadius: 8, marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>
                Noten — specificeer soort
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {NOTEN_SUB.map(sub => {
                  const actief = form.allergenen?.includes(sub);
                                    return (
                    <button key={sub} onClick={() => toggleAllergeen(sub)}
                      style={{ padding: "3px 9px", fontSize: 11, fontFamily: "inherit", cursor: "pointer", borderRadius: 10, fontWeight: actief ? 700 : 400, border: `1px solid ${actief ? C.red : C.bdr2}`, background: actief ? C.redBg : C.bg2, color: actief ? C.red : C.txt2, position: "relative" }}>
                      {sub}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

        </div>
        </InklapSectie>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PASTE VIEW
// ─────────────────────────────────────────────────────────────────────────────
function PasteView({ categorieen, onSave, onCancel, thtNorm = 3, artikelen = [] }) {
  const [tekst, setTekst] = useState("");
  const [preview, setPreview] = useState(null);
  const [geparseerd, setGeparseerd] = useState(null);
  const [fout, setFout] = useState("");

  // Live parse bij elke wijziging
  useEffect(() => {
    if (!tekst.trim()) { setGeparseerd(null); return; }
    const result = parsePasteRecept(tekst);
    if (result) {
      setGeparseerd({ ...result, allergenen: [] });
      setFout("");
    } else {
      setGeparseerd(null);
    }
  }, [tekst]);

  const verwerk = () => {
    if (!geparseerd) { setFout("Kon geen recept herkennen. Zorg dat de eerste regel de naam is, gevolgd door ingrediënten."); return; }
    setPreview({ id: uid(), ...geparseerd, categorie: "", bewaaradvies: "", thtDagen: thtNorm, foto_url: "", archived: false });
  };

  if (preview) {
    return (
      <ReceptForm
        recept={preview}
        categorieen={categorieen}
        onSave={onSave}
        onCancel={() => setPreview(null)}
        thtNorm={thtNorm}
        artikelen={artikelen}
      />
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
      {/* Links — plakken */}
      <div style={{ flex: 1, padding: 20, display: "flex", flexDirection: "column", gap: 12, borderRight: `1px solid ${C.bdr}`, minWidth: 0 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.txt, marginBottom: 4 }}>Recept plakken</div>
          <p style={{ fontSize: 11, color: C.txt3, lineHeight: 1.6 }}>
            Plak een recept hieronder. Eerste regel = naam, dan ingrediënten, dan bereiding.
          </p>
        </div>
        <textarea
          style={{ ...inp, flex: 1, minHeight: 200, resize: "none", fontFamily: "monospace", fontSize: 12, lineHeight: 1.7 }}
          placeholder={`Hamburgers\n\n500g rundergehakt\n1 ei\n2 el paneermeel\nzout en peper\n\nMeng alle ingrediënten...\nVorm balletjes en bak ze...`}
          value={tekst}
          onChange={e => setTekst(e.target.value)}
          autoFocus
        />
        {fout && <div style={{ padding: "8px 12px", background: C.redBg, border: `1px solid ${C.redBd}`, borderRadius: 6, fontSize: 12, color: C.red }}>{fout}</div>}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={verwerk} disabled={!geparseerd}
            style={{ ...btnStyle(geparseerd ? C.pri : C.bdr, geparseerd ? "#fff" : C.txt3, geparseerd ? C.pri : C.bdr), flex: 1 }}>
            <ClipboardPaste size={14} /> Opslaan & aanpassen
          </button>
          <button onClick={onCancel} style={btnStyle(C.bg3, C.txt2, C.bdr)}>Annuleren</button>
        </div>
      </div>

      {/* Rechts — live preview */}
      <div style={{ flex: 1, padding: 20, overflowY: "auto", minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.txt, marginBottom: 12 }}>Preview</div>
        {!tekst.trim() ? (
          <div style={{ color: C.txt3, fontSize: 13, fontStyle: "italic" }}>Begin met plakken om een preview te zien…</div>
        ) : !geparseerd ? (
          <div style={{ color: C.amber, fontSize: 12 }}>⚠ Geen recept herkend — zorg dat de eerste regel de naam is.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Naam */}
            <div style={{ fontSize: 20, fontWeight: 800, color: C.txt }}>{geparseerd.naam}</div>

            {/* Allergenen */}
            {geparseerd.allergenen?.length > 0 && (
              <div style={{ padding: "8px 12px", background: C.redBg, border: `1px solid ${C.redBd}`, borderRadius: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.red, marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
                  <TriangleAlert size={11} /> Allergenen
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {geparseerd.allergenen.filter(a => !GLUTEN_SUB.includes(a) && !NOTEN_SUB.includes(a)).map(a => (
                    <span key={a} style={{ background: C.bg2, border: `1px solid ${C.redBd}`, color: C.red, fontSize: 10, padding: "2px 7px", borderRadius: 10, fontWeight: 600 }}>{a}</span>
                  ))}
                </div>
                {geparseerd.allergenen.some(a => GLUTEN_SUB.includes(a)) && (
                  <div style={{ fontSize: 11, color: C.red, marginTop: 4 }}>
                    <span style={{ fontWeight: 600 }}>Gluten: </span>
                    {geparseerd.allergenen.filter(a => GLUTEN_SUB.includes(a)).join(", ")}
                  </div>
                )}
                {geparseerd.allergenen.some(a => NOTEN_SUB.includes(a)) && (
                  <div style={{ fontSize: 11, color: C.red, marginTop: 4 }}>
                    <span style={{ fontWeight: 600 }}>Noten: </span>
                    {geparseerd.allergenen.filter(a => NOTEN_SUB.includes(a)).join(", ")}
                  </div>
                )}
              </div>
            )}

            {/* Ingrediënten */}
            {geparseerd.ingredienten?.length > 0 && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                  Ingrediënten ({geparseerd.ingredienten.length})
                </div>
                {geparseerd.ingredienten.map((ing, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: `1px solid ${C.bdr}`, fontSize: 13 }}>
                    <span style={{ color: C.txt }}>{ing.naam}</span>
                    <span style={{ color: C.txt2, fontSize: 12 }}>{ing.hoeveelheid} {ing.eenheid}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Bereiding */}
            {geparseerd.bereiding && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Bereiding</div>
                <p style={{ fontSize: 12, color: C.txt, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{geparseerd.bereiding}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CATEGORIEËN VIEW
// ─────────────────────────────────────────────────────────────────────────────
function CategorieenView({ categorieen, onUpdate }) {
  const [nieuw, setNieuw] = useState("");
  const [edit, setEdit] = useState(null);

  const voegToe = () => {
    if (!nieuw.trim() || categorieen.includes(nieuw.trim())) return;
    onUpdate([...categorieen, nieuw.trim()]);
    setNieuw("");
  };
  const hernoem = () => {
    if (!edit?.naam.trim()) return;
    onUpdate(categorieen.map(c => c === edit.oud ? edit.naam : c));
    setEdit(null);
  };
  const verwijder = (cat) => {
    if (!confirm(`Categorie "${cat}" verwijderen? Recepten in deze categorie krijgen geen categorie.`)) return;
    onUpdate(categorieen.filter(c => c !== cat));
  };

  return (
    <div style={{ flex: 1, padding: 24, maxWidth: 500 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.txt, marginBottom: 16 }}>Categorieën beheren</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {categorieen.map(cat => (
          <div key={cat} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {edit?.oud === cat ? (
              <>
                <input style={{ ...inp, flex: 1 }} value={edit.naam} onChange={e => setEdit(f => ({ ...f, naam: e.target.value }))} autoFocus onKeyDown={e => e.key === "Enter" && hernoem()} />
                <button onClick={hernoem} style={btnStyle(C.pri, "#fff", C.pri)}>OK</button>
                <button onClick={() => setEdit(null)} style={btnStyle(C.bg3, C.txt2, C.bdr)}>✕</button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, fontSize: 13, color: C.txt, padding: "7px 0" }}>{cat}</span>
                <button onClick={() => setEdit({ oud: cat, naam: cat })} style={{ ...btnStyle(C.bg3, C.txt2, C.bdr), padding: "7px 10px" }}>Bewerk</button>
                <button onClick={() => verwijder(cat)} style={{ ...btnStyle(C.redBg, C.red, C.redBd), padding: "7px 10px" }}><Trash2 size={13} /></button>
              </>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.bdr}` }}>
        <input style={{ ...inp, flex: 1 }} placeholder="Nieuwe categorie…" value={nieuw} onChange={e => setNieuw(e.target.value)} onKeyDown={e => e.key === "Enter" && voegToe()} />
        <button onClick={voegToe} style={btnStyle(C.pri, "#fff", C.pri)}>+ Toevoegen</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function Sectie({ titel }) {
  return <div style={{ fontSize: 10, fontWeight: 700, color: C.txt2, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8 }}>{titel}</div>;
}
function Veld({ label, children, style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, ...style }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: C.txt2, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</label>
      {children}
    </div>
  );
}



export function recipeQuantity(value, factor = 1) {
  const original = String(value ?? "");
  if (factor === 1) return { text: original, scaled: true };
  const normalized = original.trim().replace(",", ".");
  let number;
  if (/^\d+(?:\.\d+)?$/.test(normalized)) number = Number(normalized);
  else {
    const fraction = normalized.match(/^(?:(\d+)\s+)?(\d+)\/(\d+)$/);
    if (fraction && Number(fraction[3]) > 0) number = Number(fraction[1] || 0) + Number(fraction[2]) / Number(fraction[3]);
  }
  if (!Number.isFinite(number)) return { text: original, scaled: false };
  return { text: new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 6 }).format(number * factor), scaled: true };
}
const RECIPE_STYLES = `
.kc-recipe-browser{background:#eff5f2;gap:16px;padding:16px}
.kc-recipe-browser>div{border-radius:14px;background:white}
.kc-recipe-list-title{padding:18px 16px 2px;background:white}.kc-recipe-list-title h2{font-size:24px;margin:0;color:#102a36}
.kc-recipe-browser button:focus-visible,.kc-recipe-sheet button:focus-visible,.kc-recipe-sheet summary:focus-visible{outline:3px solid #0b8062;outline-offset:-3px}
.kc-recipe-row:hover{filter:brightness(.98)}
.kc-recipe-sheet{height:100%;min-width:0;display:flex;flex-direction:column;overflow:hidden;background:#fff;color:#142e3a;font-family:system-ui,-apple-system,sans-serif;font-size:16px}
.kc-recipe-heading{padding:24px;display:flex;align-items:flex-start;justify-content:space-between;gap:20px;flex-wrap:wrap;border-bottom:1px solid #e1e9e5;background:white}
.kc-recipe-heading-copy{flex:1 1 240px;min-width:0}.kc-recipe-heading h1{font-size:clamp(24px,2.4vw,36px);line-height:1.2;margin:0;overflow-wrap:anywhere;letter-spacing:-.7px}.kc-recipe-heading-copy p{margin:8px 0 0;color:#526771;font-size:16px}
.kc-recipe-actions{display:flex;align-items:center;flex-wrap:wrap;gap:8px}.kc-recipe-actions button,.kc-recipe-more summary{display:flex;align-items:center;justify-content:center;gap:7px;min-height:44px;padding:10px 13px;border:1px solid #dae5df;background:#f8faf9;border-radius:8px;color:#183a46;font:600 14px system-ui;cursor:pointer}.kc-recipe-actions .kc-recipe-edit{background:#edf8f2;color:#08664f;border-color:#85bba5}
.kc-recipe-more{position:relative}.kc-recipe-more summary{list-style:none}.kc-recipe-more summary::-webkit-details-marker{display:none}.kc-recipe-more>div{position:absolute;right:0;top:50px;z-index:5;padding:8px;min-width:180px;background:white;border:1px solid #dce5df;box-shadow:0 8px 25px #102a3620;border-radius:10px}.kc-recipe-more button{width:100%;justify-content:flex-start;border:0;background:white}.kc-recipe-more .kc-recipe-delete{color:#b42318}
.kc-recipe-scroll{flex:1;min-height:0;overflow:auto;padding:24px;overscroll-behavior:contain}.kc-recipe-notices{display:flex;flex-wrap:wrap;gap:12px;margin-bottom:28px}.kc-recipe-allergens,.kc-recipe-storage{display:flex;align-items:center;gap:10px;padding:14px 16px;border-radius:10px;flex:1 1 240px;font-size:15px}.kc-recipe-allergens{background:#fffbef;border:1px solid #f2dba5;color:#805013;flex-wrap:wrap}.kc-recipe-allergens>div{display:flex;flex-wrap:wrap;gap:6px}.kc-recipe-allergens span{padding:3px 8px;border:1px solid #e2c790;border-radius:6px;background:white;color:#614518}.kc-recipe-storage{background:#eef7f2;color:#245a48}
.kc-recipe-columns{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,320px),1fr));gap:36px;align-items:start}.kc-recipe-columns section{min-width:0}.kc-recipe-columns h2{font-size:24px;line-height:1.3;margin:0 0 20px}.kc-recipe-factor{display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:12px;border:1px solid #dce5df;border-radius:10px;margin-bottom:24px;font-size:14px}.kc-factor-options{display:flex;gap:3px}.kc-factor-options button{min-width:42px;min-height:42px;background:#f6f9f7;color:#184f3e;border:1px solid #dce5df;border-radius:6px;font:600 15px system-ui;cursor:pointer}.kc-factor-options button[aria-pressed=true]{background:#08664f;color:#fff}.kc-recipe-factor label{display:flex;align-items:center;gap:8px;font-size:13px;color:#526771}.kc-recipe-factor input{width:80px;padding:9px 6px;font:inherit;border:1px solid #a6bdb2;border-radius:6px;min-height:42px}.kc-recipe-note{font-size:14px;line-height:1.5;color:#526771;margin:0 0 16px}
.kc-recipe-ingredients{list-style:none;margin:0;padding:0;border:1px solid #e1e9e5;border-radius:10px;overflow:hidden}.kc-recipe-ingredients li{display:flex;justify-content:space-between;gap:20px;padding:15px 16px;border-bottom:1px solid #e1e9e5;line-height:1.5}.kc-recipe-ingredients li:last-child{border:0}.kc-recipe-ingredients li span:first-child{overflow-wrap:anywhere}.kc-recipe-ingredients li span:last-child{color:#526771;text-align:right;font-variant-numeric:tabular-nums;flex-shrink:0}
.kc-recipe-steps{list-style:none;padding:0;margin:0}.kc-recipe-steps li{display:flex;gap:16px;padding:4px 0 24px;margin-bottom:20px;border-bottom:1px solid #e1e9e5}.kc-recipe-steps li>span{display:flex;align-items:center;justify-content:center;width:36px;height:36px;flex-shrink:0;border-radius:50%;background:#e4f4eb;color:#08664f;font-weight:700}.kc-recipe-steps p{margin:4px 0 0;font-size:17px;line-height:1.8;white-space:pre-wrap;overflow-wrap:anywhere}
.kc-kitchen-dialog{position:fixed;inset:0;width:100%;height:100%;max-width:none;max-height:none;margin:0;padding:0;border:0;background:white}.kc-kitchen-dialog::backdrop{background:#102a36}.kc-kitchen{font-size:22px}.kc-kitchen .kc-recipe-heading h1{font-size:clamp(28px,3vw,44px)}.kc-kitchen .kc-recipe-scroll{padding:32px clamp(20px,4vw,80px)}.kc-kitchen .kc-recipe-columns{gap:48px;grid-template-columns:repeat(auto-fit,minmax(min(100%,380px),1fr))}.kc-kitchen .kc-recipe-steps p{font-size:23px;line-height:1.8}.kc-kitchen .kc-recipe-columns h2{font-size:30px}.kc-wake-status{margin:0;padding:10px 24px;background:#eef7f2;color:#245a48;font-size:14px}
@media(max-width:1100px){.kc-recipe-browser{gap:8px;padding:8px}.kc-recipe-heading,.kc-recipe-scroll{padding:20px}.kc-recipe-columns{gap:24px}}
@media(max-width:768px){.kc-recipe-browser{padding:0;gap:0}.kc-recipe-heading{padding:16px;gap:14px}.kc-recipe-scroll{padding:16px}.kc-recipe-actions{gap:6px}.kc-recipe-actions button{padding:9px;font-size:13px}.kc-recipe-columns{grid-template-columns:1fr;gap:28px}.kc-kitchen .kc-recipe-scroll{padding:20px}.kc-kitchen .kc-recipe-columns{grid-template-columns:1fr;gap:28px}.kc-recipe-ingredients li{gap:12px}.kc-recipe-notices{margin-bottom:24px}}
`;


